/**
 * Assessment API
 *
 * RTK Query endpoints for authority assessment sessions.
 */

import { api } from '../api';

// ============================================
// TYPES
// ============================================

export type DifficultyLevel = 'beginner' | 'intermediate' | 'advanced';
export type SessionStatus = 'active' | 'completed' | 'abandoned';
export type TurnType = 'opener' | 'followup' | 'clarification';

export interface UserContext {
  userName?: string;
  userRole?: string;
  userExperience?: string;
}

export interface ExpertPersona {
  name: string;
  title: string;
  bio: string;
}

export interface AssessmentSession {
  id: string;
  objectiveId: string;
  domainKey: string | null;
  categoryKey: string | null;
  difficulty: DifficultyLevel | null;
  status: SessionStatus;
  turnCount: number;
  templateId?: string | null;
  knowledgeScore?: number | null;
  deliveryScore?: number | null;
  authorityScore?: number | null;
  passed?: boolean | null;
  feedback?: string | null;
  startedAt: string;
  completedAt?: string | null;
  objective?: {
    id: string;
    objectiveText: string;
    category?: {
      id: string;
      name: string;
    } | null;
  } | null;
}

export interface AssessmentTurn {
  id: string;
  turnNumber: number;
  turnType: TurnType;
  questionText: string;
  responseText?: string | null;
  responseDurationSeconds?: number | null;
  questionAskedAt: string;
  responseReceivedAt?: string | null;
}

export interface StartSessionRequest {
  objectiveId: string;
  userContext?: UserContext;
}

export interface StartSessionResponse {
  session: {
    id: string;
    objectiveId: string;
    domainKey: string | null;
    categoryKey: string | null;
    difficulty: DifficultyLevel;
    status: SessionStatus;
    turnCount: number;
    templateId: string | null;
  };
  openingQuestion: string;
  expertPersona: ExpertPersona | null;
  maxTurns: number;
}

export interface SubmitAnswerRequest {
  sessionId: string;
  responseText: string;
  responseAudioUrl?: string;
  responseDurationSeconds?: number;
}

export interface SubmitAnswerResponse {
  session: {
    id: string;
    status: SessionStatus;
    turnCount: number;
  };
  currentTurn: {
    turnNumber: number;
    turnType: TurnType;
    responseReceivedAt: string;
  };
  shouldFinalize: boolean;
  nextQuestion: string | null;
}

export interface FinalizeSessionRequest {
  sessionId: string;
  knowledgeScore?: number;
  deliveryScore?: number;
  feedback?: string;
}

export interface FinalizeSessionResponse {
  session: {
    id: string;
    status: SessionStatus;
    // R0.2: null = not measured. `passed` is null when the assessment could not
    // be scored — distinct from false, which would read as "failed".
    knowledgeScore: number | null;
    deliveryScore: number | null;
    authorityScore: number | null;
    passed: boolean | null;
    scoringVersion?: string;
    unavailableDimensions?: Array<{
      key: string;
      reason: string;
      evaluatorKey: string;
      evaluatorVersion: string;
    }>;
    feedback: string;
    completedAt: string;
  };
  suggestions: {
    nextDifficulty: DifficultyLevel | null;
    message: string;
    suggestedObjectives: Array<{
      id: string;
      objectiveText: string;
    }>;
  };
}

export interface SessionWithTurns {
  session: AssessmentSession;
  turns: AssessmentTurn[];
}

export interface ActiveSessionCheck {
  hasActiveSession: boolean;
  session: {
    id: string;
    status: SessionStatus;
    turnCount: number;
    startedAt: string;
    turns: Array<{
      turnNumber: number;
      questionText: string;
      hasResponse: boolean;
    }>;
  } | null;
}

export interface UserSessionStats {
  totalSessions: number;
  completedSessions: number;
  /** Completed sessions with a measured pass. Absent from an older server. */
  passedSessions?: number;
  /**
   * False while no evidence-based evaluator exists (RC19): skill practice runs
   * are recorded but NOT scored, so no pass is possible. Absent from an older server.
   */
  scoringAvailable?: boolean;
  averageScore: number | null;
  byDifficulty: Record<
    DifficultyLevel,
    {
      count: number;
      avgScore: number | null;
    }
  >;
}

export interface RecommendedDifficulty {
  objectiveId: string;
  recommendedDifficulty: DifficultyLevel;
}

// API Response wrapper
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// ============================================
// API ENDPOINTS
// ============================================

export const assessmentApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // ----------------------------------------
    // SESSION MANAGEMENT
    // ----------------------------------------

    // Start a new assessment session
    startAssessment: builder.mutation<StartSessionResponse, StartSessionRequest>({
      query: (body) => ({
        url: '/assessments',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<StartSessionResponse>) => response.data,
      invalidatesTags: ['Assessments', 'SkillProgress'],
    }),

    // Submit an answer
    submitAnswer: builder.mutation<SubmitAnswerResponse, SubmitAnswerRequest>({
      query: ({ sessionId, ...body }) => ({
        url: `/assessments/${sessionId}/answer`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<SubmitAnswerResponse>) => response.data,
      invalidatesTags: (_result, _error, { sessionId }) => [
        { type: 'Assessments', id: sessionId },
      ],
    }),

    // Finalize assessment
    finalizeAssessment: builder.mutation<FinalizeSessionResponse, FinalizeSessionRequest>({
      query: ({ sessionId, ...body }) => ({
        url: `/assessments/${sessionId}/finalize`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<FinalizeSessionResponse>) => response.data,
      invalidatesTags: (_result, _error, { sessionId }) => [
        'Assessments',
        'SkillProgress',
        { type: 'Assessments', id: sessionId },
      ],
    }),

    // Abandon assessment
    abandonAssessment: builder.mutation<{ session: { id: string; status: string } }, string>({
      query: (sessionId) => ({
        url: `/assessments/${sessionId}/abandon`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<{ session: { id: string; status: string } }>) =>
        response.data,
      invalidatesTags: ['Assessments'],
    }),

    // ----------------------------------------
    // UTILITY ENDPOINTS
    // ----------------------------------------

    // Check for active session on an objective
    getActiveSession: builder.query<ActiveSessionCheck, string>({
      query: (objectiveId) => `/assessments/active/${objectiveId}`,
      transformResponse: (response: ApiResponse<ActiveSessionCheck>) => response.data,
    }),

    // Get user's assessment stats
    getAssessmentStats: builder.query<UserSessionStats, void>({
      query: () => '/assessments/stats',
      transformResponse: (response: ApiResponse<UserSessionStats>) => response.data,
      providesTags: ['Assessments'],
    }),

    // Get recommended difficulty for an objective
    getRecommendedDifficulty: builder.query<RecommendedDifficulty, string>({
      query: (objectiveId) => `/assessments/difficulty/${objectiveId}`,
      transformResponse: (response: ApiResponse<RecommendedDifficulty>) => response.data,
    }),
  }),
  overrideExisting: false,
});

// ============================================
// EXPORTED HOOKS
// ============================================

export const {
  // Mutations
  useStartAssessmentMutation,
  useSubmitAnswerMutation,
  useFinalizeAssessmentMutation,
  useAbandonAssessmentMutation,
  // Queries
  useGetActiveSessionQuery,
  useGetAssessmentStatsQuery,
  useGetRecommendedDifficultyQuery,
} = assessmentApi;
